import { Job, SearchResults } from "../../Shared/scriptts/interfaces";
import { getUserObject, setUserObject } from "../../Shared/scriptts/session/sessionmain";
import { params, signinClick } from '../search-main';
import { logSaveJob, logSearchConversion } from "../../Shared/scriptts/analytics";

document.addEventListener("DOMContentLoaded", () => {
    const saveJobData = sessionStorage.getItem("saveJobAfterSignIn");
    if (saveJobData) {
        sessionStorage.removeItem("saveJobAfterSignIn");
        try {
            const {docId, save, joa} = JSON.parse(saveJobData);
            waitForSaveJob(docId, save, joa);
        } catch (error) {
            console.error("Failed to parse saveJobData from sessionStorage:", error);
        }
    }

});
/**
 * Builds search results cards and adds them to the results element.
 * @param data The data returned from the search api
 * @returns void
 */
export function buildSearchResultsCards(data: SearchResults) {
    const userObject = getUserObject();
    const resultsElem = document.querySelector<HTMLElement>('#search-results');
    if (!resultsElem) {
        return;
    }
    let resultsHtml = '';

    data.Jobs?.forEach((job, i) => {
        const isSaved = job.DocumentID in userObject.SavedJobs ? true : false;
        const saveJobButtonHtml = `<button class="flex whitespace-nowrap flex-nowrap items-center cursor-pointer text-sm" data-save-job="${job.DocumentID}" data-save="${!isSaved}">
                <svg class="w-4 h-4 fill-warning-dark mr-1" role="presentation"><use xlink:href="/img/icons/sprite.svg#${isSaved ? "star" : "star_outline"}" /></svg>
                ${isSaved ? "Saved" : "Save job"}
            </button>`

        resultsHtml += `<div class="border border-gray-lighter bg-white p-4">
            <div class="flex justify-between items-start">
                <h2 class="font-normal mr-2 mt-0"><a href="/job/${job.DocumentID}" data-search-result="${i}" data-document-id="${job.DocumentID}" class="no-underline">${job.Title}</a></h2>
                ${saveJobButtonHtml}
            </div>
            <div class="w-full md:flex md:justify-between md:gap-x-2">
                <div class="basis-2/3">
                    <p><strong>${job.Agency}</strong></p><p>${job.Department}</p><p>${job.Location}</p>${(job.SubAgency) ? `<p>${job.SubAgency}</p>` : ''}
                </div>
                <div class="text-gray-dark basis-1/3">
                    <p>${job.SalaryDisplay}</p><p>${getWorkTypeDisplay(job)}</p>
                    <p class="text-sm flex flex-wrap items-center gap-x-2"><span class="md:hidden">This job open to:</span>${getHiringPathDisplay(job)}</p>
                </div>
            </div>
            <div class="mt-4 text-sm italic text-gray-dark flex flex-nowrap items-center gap-x-1">
                <svg class="w-4 h-4 fill-${getClockColor(job)}" role="presentation"><use xlink:href="/img/icons/sprite.svg#schedule" /></svg> ${job.DateDisplay}
            </div>
        </div>`;
    });
    resultsElem.innerHTML = resultsHtml;
    document.querySelectorAll<HTMLElement>(`[data-search-result]`).forEach(elem => elem.addEventListener('click', evt => searchResultClicked(evt, data)));

    data.Jobs?.forEach(job => {
        const saveJobElem = document.querySelector<HTMLButtonElement>(`[data-save-job="${job.DocumentID}"]`);
        if (!saveJobElem) {
            return;
        }
        saveJobElem.addEventListener('click', evt => saveJobClicked(saveJobElem, evt, job));
    });
}

function searchResultClicked(evt: Event, results: SearchResults) {
    const elem = <HTMLElement>(evt.currentTarget);
    const index = elem.getAttribute('data-search-result') || '';
    const documentId = elem.getAttribute('data-document-id') || '';

    if (index === '' || documentId === '') {
        return;
    }

    const userObject = getUserObject();
    userObject.SearchResults = {
        Params: params,
        SelectedDocumentId: documentId,
        ConversionId: crypto.randomUUID(),
        Results: {
            Items: [],
            ResultCount: params.TotalResults || 0,
            ReturnUrl: window.location.href,
            PageSets: []
        }
    };
    
    userObject.SearchResults.Params.ResultsPerPage = results.Pager.ItemsPerPage;

    document.querySelectorAll<HTMLElement>('[data-search-result]').forEach(elem => {
        userObject.SearchResults.Results.Items.push(elem.getAttribute('data-document-id') || '');
    });
    userObject.SearchResults.Results.PageSets.push({
        Page: params.Page === undefined ? 1 : Number(params.Page),
        PageItems: userObject.SearchResults.Results.Items
    });
    
    setUserObject(userObject);
    logSearchConversion(userObject, "search_conversion_click");
}

function saveJobClicked(elem: HTMLButtonElement, event: Event, joa: Job) {
    const docId = Number(elem.getAttribute('data-save-job'));
    const save = elem.getAttribute('data-save');

    const userObject = getUserObject();
    if (!userObject.IsAuthenticated) {
        sessionStorage.setItem("saveJobAfterSignIn", JSON.stringify({ docId, save, joa }));
        signinClick(event);
        return;
    } else {
        saveJob(docId, save, elem, joa);
    }
}

function waitForSaveJob(docId: number, save: string, joa: Job) {
    const selector = `[data-save-job="${docId}"]`;

    // save immediately in case the element is already present
    const elem = document.querySelector<HTMLButtonElement>(selector);
    if (elem) {
        saveJob(docId, save, elem, joa);
        return;
    }

    // Otherwise, observe DOM changes until the element appears
    const observer = new MutationObserver(() => {
        const elem = document.querySelector<HTMLButtonElement>(selector);
        if (elem) {
            observer.disconnect();
            saveJob(docId, save, elem, joa);
        }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    const timeoutId = setTimeout(() => {
        observer.disconnect();
        console.warn(`Timeout: Element with selector "${selector}" not found within 10 seconds.`);
    }, 10000); 
}

function saveJob(docId: number, save: string | null, elem: HTMLButtonElement | null, joa: Job) {
    if (save === null || isNaN(docId) || elem === null) {
        return;
    };

    fetch(`/search/savejob/${docId}/${save}`, {
        method: 'POST',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json'
        }
    })
        .then(response => {
            if (!response.ok) {
                throw new Error(`Fetch error: ${response.status}`);
            }
        })
        .then(() => {
            var userObject = getUserObject();
            if (save === "true") {
                elem.setAttribute('data-save', 'false');
                userObject.SavedJobs[docId] = true;
            }
            else {
                elem.setAttribute('data-save', 'true');
                delete userObject.SavedJobs[docId];
            }
            elem.innerHTML = `<svg class="w-4 h-4 fill-warning-dark mr-1" role="presentation"><use xlink:href="/img/icons/sprite.svg#${save === 'true' ? 'star' : 'star_outline'}" /></svg> ${save === 'true' ? 'Saved' : 'Save job'}`;
            setUserObject(userObject);
            logSaveJob(joa, (save === "true" ? true : false));
        })
        .catch(err => console.error(err));
}

function getWorkTypeDisplay(job: Job): string {
    if (job.WorkType) {
        if (job.WorkSchedule) {
            return `${job.WorkType} &bull; ${job.WorkSchedule}`;
        }
        else {
            return job.WorkType;
        }
    }
    else if (job.WorkSchedule) {
        return job.WorkSchedule;
    }
    return '';
}

function getHiringPathDisplay(job: Job): string {
    let hpDisplay = '';
    job.HiringPath.forEach(hp => hpDisplay += `<svg class="w-7 h-7" role="img" aria-label="${hp.Tooltip}"><title>${hp.Tooltip}</title><use xlink:href="/img/icons/hiringpaths.svg#${hp.Code}" /></svg>`);
    return hpDisplay;
}

function getClockColor(job: Job): string {
    switch (job.ClockDisplay) {
        case 'job-closing-in-7':
            return 'warning';
        case 'job-closing-in-2':
            return 'emergency';
        default:
            return 'gray-dark';
    }
}
